import { makeObservable, observable, runInAction } from 'mobx';
import { nanoid } from 'nanoid';
import HttpClient from '../util/HttpClient';
import Service from '../util/Service';
import Tracker from '../classes/Tracker';
export default class ClientStore {
    constructor(config) {
        this.processing = false;
        this.error = undefined;
        makeObservable(this, {
            error: observable,
            processing: observable,
        });
        this.config = config;
        this.service = new Service();
        this.tracker = new Tracker();
        const { apiUri, agentToken, gatewayToken, traceid, fetch: altfetch, } = config;
        this.clienttraceid = traceid || nanoid();
        this.httpClient = new HttpClient(apiUri, {
            ...(agentToken ? { 'x-agent-token': agentToken } : {}),
            ...(gatewayToken ? { Authorization: `Bearer ${gatewayToken}` } : {}),
        }, altfetch);
    }
    $getTracingHeaders() {
        const requesttraceid = nanoid(); // a new one for each single request
        return {
            'tef-coms-clienttraceid': this.clienttraceid,
            'tef-coms-traceid': requesttraceid,
            activityid: `${this.clienttraceid}|${requesttraceid}`, // for api gateway, ignored by COMS
        };
    }
    isRaitt() {
        return !!this.config.spId;
    }
    getSpId() {
        var _a;
        return (this.isRaitt() ? this.config.spId : (_a = this.identity) === null || _a === void 0 ? void 0 : _a.spId);
    }
    getLcId() {
        var _a;
        return this.isRaitt() ? this.config.lcId : (_a = this.identity) === null || _a === void 0 ? void 0 : _a.lcId;
    }
    getRegisterId() {
        var _a;
        return this.isRaitt() ? this.config.registerId : (_a = this.identity) === null || _a === void 0 ? void 0 : _a.registerId;
    }
    async $processPromise(func, eventNameSuccess, eventNameError, eventMessageSuccess, eventMessageError) {
        runInAction(() => {
            this.processing = true;
            this.error = undefined;
        });
        try {
            const data = await func();
            this.service.fireEvent({
                name: eventNameSuccess,
                level: 'info',
                message: eventMessageSuccess,
            });
            return data;
        }
        catch (error) {
            this.service.fireEvent({
                name: eventNameError,
                level: 'error',
                message: eventMessageError,
                error: error,
            });
            this.tracker.comsError(eventMessageSuccess);
            runInAction(() => {
                this.error = error;
            });
            throw error;
        }
        finally {
            runInAction(() => {
                this.processing = false;
            });
        }
    }
    async getIdentity() {
        return this.$processPromise(this.$getIdentity.bind(this), 'auth/VALIDATE', 'auth/ERROR', 'token validation successful', 'token validation failed');
    }
    async $getIdentity() {
        const path = this.isRaitt() ? '/identity' : '/agentinfo';
        const headers = {
            ...this.$getTracingHeaders(),
        };
        this.identity = await this.httpClient.get(path, undefined, headers);
        return this.identity;
    }
    checkIdentity() {
        if (!this.identity) {
            throw new Error('identity is not set');
        }
    }
    async getConsentHistory(configId, offset, limit) {
        this.checkIdentity();
        return this.$processPromise(this.$getConsentHistory.bind(this, configId, offset, limit), 'history/LOAD', 'history/ERROR', 'loading history successful', 'loading history failed');
    }
    async $getConsentHistory(configId, offset, limit) {
        const spId = this.getSpId();
        const lcId = this.getLcId();
        const path = '/service/history';
        const query = {
            lcId,
            spId,
            configId,
            offset,
            limit,
        };
        const headers = {
            ...this.$getTracingHeaders(),
        };
        return this.httpClient.get(path, query, headers);
    }
    async loadConsents() {
        this.checkIdentity();
        const { isExistingRegister } = this.config;
        // bugfix #247: if isExistingRegister is set GET register should be called in any case
        const lcId = this.getLcId();
        return this.$processPromise(lcId && !isExistingRegister
            ? this.$getConsentCustomer.bind(this)
            : this.$getConsentRegister.bind(this), 'consents/LOAD', 'consents/ERROR', 'loading consents successful', 'loading consents failed');
    }
    async $getConsentCustomer() {
        const spId = this.getSpId();
        const lcId = this.getLcId();
        const { lifecycle, consentwhitelist } = this.config;
        const path = `/consent/sp/${spId}/customer/${lcId}`;
        const query = {
            lifecycle,
            textformat: 'html',
            ignoreNotFound: true,
            extended: true,
            additionalInfo: true,
            pageformat: true,
        };
        const headers = {
            ...this.$getTracingHeaders(),
            ...(consentwhitelist
                ? { 'tef-coms-comment': `consentwhitelist:${consentwhitelist}` }
                : {}),
        };
        return this.httpClient.get(path, query, headers);
    }
    async $getConsentRegister() {
        const spId = this.getSpId();
        const registerId = this.getRegisterId();
        const { lifecycle, consentwhitelist, isExistingRegister } = this.config;
        const path = isExistingRegister
            ? `/consent/sp/${spId}/register/${registerId}`
            : `/consent/sp/${spId}/register`;
        const query = {
            lifecycle,
            textformat: 'html',
            extended: true,
            additionalInfo: true,
            pageformat: true,
        };
        const headers = {
            ...this.$getTracingHeaders(),
            ...(consentwhitelist
                ? { 'tef-coms-comment': `consentwhitelist:${consentwhitelist}` }
                : {}),
        };
        return this.httpClient.get(path, query, headers);
    }
    async saveConsents(consents, references = []) {
        this.checkIdentity();
        return this.$processPromise(this.$saveConsents.bind(this, consents, references), 'consents/SAVE', 'consents/ERROR', 'saving consents successful', 'saving consents failed');
    }
    async $saveConsents(consents, references) {
        var _a, _b;
        const { lifecycle, consentwhitelist, channel, usertype, vonr, } = this.config;
        const spId = this.getSpId();
        const lcId = this.getLcId();
        const registerId = this.getRegisterId();
        const username = (_b = (_a = this.identity) === null || _a === void 0 ? void 0 : _a.username) !== null && _b !== void 0 ? _b : 'undefined';
        const path = lcId
            ? `/consent/sp/${spId}/customer/${lcId}`
            : `/consent/sp/${spId}/register/${registerId}`;
        const query = { lifecycle };
        if (registerId && lcId) {
            query.registerId = registerId;
        }
        const headers = {
            ...this.$getTracingHeaders(),
            'tef-coms-username': vonr ? `${vonr} | ${username}` : username,
            'tef-coms-usertype': usertype || 'Agent',
            'tef-coms-channel': channel || 'Shop',
            ...(consentwhitelist
                ? { 'tef-coms-comment': `consentwhitelist:${consentwhitelist}` }
                : {}),
            ...(references.length
                ? { 'tef-coms-references': JSON.stringify(references) }
                : {}),
        };
        await this.httpClient.post(path, consents, query, headers);
    }
}
//# sourceMappingURL=ClientStore.js.map